/**
 * Settings Logger
 *
 * Centralized logging for Settings operations
 * Helps with debugging and monitoring in development/production
 */

import featureFlags from '../config/featureFlags';

/**
 * Log levels
 */
const LOG_LEVELS = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
};

/**
 * Settings Logger Class
 */
class SettingsLogger {
  constructor() {
    this.enabled = featureFlags.settingsLogging;
    this.debugEnabled = featureFlags.debugSettingsTransform;
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  /**
   * Check if logging is enabled for given level
   * @param {string} level - Log level
   * @returns {boolean}
   */
  isEnabled(level = LOG_LEVELS.INFO) {
    // Always log errors
    if (level === LOG_LEVELS.ERROR) {
      return true;
    }

    // Debug logs only if debug flag is on
    if (level === LOG_LEVELS.DEBUG) {
      return this.debugEnabled;
    }

    // Other logs based on settings logging flag
    return this.enabled;
  }

  /**
   * Log API call
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request data
   */
  logApiCall(method, endpoint, data = null) {
    if (!this.isEnabled(LOG_LEVELS.INFO)) return;

    const timestamp = new Date().toISOString();
    console.log(
      `%c[Settings API] ${timestamp}`,
      'color: #3b82f6; font-weight: bold',
      `${method} ${endpoint}`,
      data ? data : ''
    );
  }

  /**
   * Log API response
   * @param {string} method - HTTP method
   * @param {string} endpoint - API endpoint
   * @param {Object} response - API response
   * @param {number} duration - Request duration in ms
   */
  logApiResponse(method, endpoint, response, duration = null) {
    if (!this.isEnabled(LOG_LEVELS.INFO)) return;

    const durationStr = duration ? ` (${duration}ms)` : '';
    console.log(
      `%c[Settings API] Response`,
      'color: #10b981; font-weight: bold',
      `${method} ${endpoint}${durationStr}`,
      response
    );
  }

  /**
   * Log API error
   * @param {string} operation - Operation name
   * @param {Error} error - Error object
   * @param {Object} context - Additional context
   */
  logError(operation, error, context = {}) {
    const timestamp = new Date().toISOString();

    console.error(
      `%c[Settings Error] ${timestamp}`,
      'color: #ef4444; font-weight: bold',
      operation
    );

    console.error('Error:', error);

    if (Object.keys(context).length > 0) {
      console.error('Context:', context);
    }

    // Send to monitoring service in production
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error, {
        tags: {
          component: 'settings',
          operation
        },
        contexts: {
          settings: context
        }
      });
    }
  }

  /**
   * Log transformation operation
   * @param {string} from - Source format
   * @param {string} to - Target format
   * @param {Object} input - Input data
   * @param {Object} output - Output data
   */
  logTransformation(from, to, input, output) {
    if (!this.isEnabled(LOG_LEVELS.DEBUG)) return;

    console.group(`%c[Settings Transform] ${from} → ${to}`, 'color: #8b5cf6; font-weight: bold');
    console.log('Input:', input);
    console.log('Output:', output);
    console.groupEnd();
  }

  /**
   * Log feature flag usage
   * @param {string} flag - Flag name
   * @param {boolean} value - Flag value
   */
  logFeatureFlag(flag, value) {
    if (!this.isEnabled(LOG_LEVELS.DEBUG)) return;

    const symbol = value ? '✅' : '❌';
    console.log(
      `%c[Feature Flag] ${symbol} ${flag}`,
      `color: ${value ? '#10b981' : '#6b7280'}`,
      value
    );
  }

  /**
   * Log performance metric
   * @param {string} operation - Operation name
   * @param {number} duration - Duration in ms
   * @param {Object} metadata - Additional metadata
   */
  logPerformance(operation, duration, metadata = {}) {
    if (!this.isEnabled(LOG_LEVELS.INFO)) return;

    const threshold = 1000; // 1 second
    const isSlow = duration > threshold;

    console.log(
      `%c[Settings Performance] ${operation}`,
      `color: ${isSlow ? '#f59e0b' : '#6b7280'}`,
      `${duration}ms`,
      metadata
    );

    // Warn if slow
    if (isSlow) {
      console.warn(`⚠️  Slow operation: ${operation} took ${duration}ms`);
    }
  }

  /**
   * Log validation error
   * @param {string} field - Field name
   * @param {string} message - Error message
   */
  logValidationError(field, message) {
    if (!this.isEnabled(LOG_LEVELS.WARN)) return;

    console.warn(
      `%c[Settings Validation]`,
      'color: #f59e0b; font-weight: bold',
      `Field "${field}": ${message}`
    );
  }

  /**
   * Log cache operation
   * @param {string} operation - Operation (hit, miss, set, clear)
   * @param {string} key - Cache key
   */
  logCache(operation, key) {
    if (!this.isEnabled(LOG_LEVELS.DEBUG)) return;

    const symbols = {
      hit: '✅',
      miss: '❌',
      set: '📝',
      clear: '🗑️'
    };

    console.log(
      `%c[Settings Cache] ${symbols[operation] || ''}`,
      'color: #6366f1',
      operation.toUpperCase(),
      key
    );
  }

  /**
   * Log batch operation summary
   * @param {string} operation - Operation type
   * @param {number} total - Total items
   * @param {number} succeeded - Successful items
   * @param {number} failed - Failed items
   */
  logBatchSummary(operation, total, succeeded, failed) {
    if (!this.isEnabled(LOG_LEVELS.INFO)) return;

    console.group(`%c[Settings Batch] ${operation}`, 'color: #06b6d4; font-weight: bold');
    console.log(`Total: ${total}`);
    console.log(`✅ Succeeded: ${succeeded}`);
    if (failed > 0) {
      console.log(`❌ Failed: ${failed}`);
    }
    console.groupEnd();
  }

  /**
   * Log sync status
   * @param {string} status - Sync status (pending, syncing, synced, error)
   * @param {Object} details - Additional details
   */
  logSync(status, details = {}) {
    if (!this.isEnabled(LOG_LEVELS.INFO)) return;

    const symbols = {
      pending: '⏳',
      syncing: '🔄',
      synced: '✅',
      error: '❌'
    };

    const colors = {
      pending: '#f59e0b',
      syncing: '#3b82f6',
      synced: '#10b981',
      error: '#ef4444'
    };

    console.log(
      `%c[Settings Sync] ${symbols[status]} ${status.toUpperCase()}`,
      `color: ${colors[status]}; font-weight: bold`,
      details
    );
  }

  /**
   * Log settings diff
   * @param {Object} before - Settings before change
   * @param {Object} after - Settings after change
   */
  logDiff(before, after) {
    if (!this.isEnabled(LOG_LEVELS.DEBUG)) return;

    const changes = this.computeDiff(before, after);

    if (changes.length === 0) {
      console.log('%c[Settings Diff] No changes', 'color: #6b7280');
      return;
    }

    console.group('%c[Settings Diff] Changes detected', 'color: #8b5cf6; font-weight: bold');
    changes.forEach(change => {
      console.log(
        `${change.path}:`,
        `%c${JSON.stringify(change.before)}`,
        'color: #ef4444',
        '→',
        `%c${JSON.stringify(change.after)}`,
        'color: #10b981'
      );
    });
    console.groupEnd();
  }

  /**
   * Compute diff between two objects
   * @param {Object} before - Before state
   * @param {Object} after - After state
   * @returns {Array} Array of changes
   */
  computeDiff(before, after) {
    const changes = [];

    const compare = (obj1, obj2, path = '') => {
      const keys = new Set([...Object.keys(obj1 || {}), ...Object.keys(obj2 || {})]);

      for (const key of keys) {
        const fullPath = path ? `${path}.${key}` : key;
        const val1 = obj1?.[key];
        const val2 = obj2?.[key];

        if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
          compare(val1, val2, fullPath);
        } else if (val1 !== val2) {
          changes.push({
            path: fullPath,
            before: val1,
            after: val2
          });
        }
      }
    };

    compare(before, after);
    return changes;
  }

  /**
   * Start performance timer
   * @param {string} operation - Operation name
   * @returns {Function} Stop function
   */
  startTimer(operation) {
    const start = performance.now();

    return (metadata = {}) => {
      const duration = Math.round(performance.now() - start);
      this.logPerformance(operation, duration, metadata);
      return duration;
    };
  }

  /**
   * Create a scoped logger for specific operation
   * @param {string} operation - Operation name
   * @returns {Object} Scoped logger methods
   */
  scope(operation) {
    return {
      start: () => this.startTimer(operation),
      info: (message, data) => this.logApiCall('INFO', operation, { message, ...data }),
      error: (error, context) => this.logError(operation, error, context),
      warn: (message) => this.logValidationError(operation, message),
      debug: (data) => {
        if (this.isEnabled(LOG_LEVELS.DEBUG)) {
          console.log(`%c[${operation}]`, 'color: #6b7280', data);
        }
      }
    };
  }
}

// Create singleton instance
const settingsLogger = new SettingsLogger();

// Export both instance and class
export { SettingsLogger, LOG_LEVELS };
export default settingsLogger;
